import { InboxOutlined } from "@ant-design/icons";
import {
    Button,
    Card,
    DatePicker,
    Form,
    Input,
    Modal,
    notification,
    Select,
    Skeleton,
    Tooltip,
    Upload,
} from "antd";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useLocation } from "react-router-dom";
import { SettingContext } from "../../routes";
import HTTP from "../../services/httpService";
import { downloadCSV, formatDate, getUserRole } from "../../services/util";
import CustomHeader from "../common/Header";
import CustomTable from "../common/Table";

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

function SupportDetailsTable() {
    const { state } = useLocation();
    const { selectedHotels, setSelectedHotels } = useContext(SettingContext);
    const hotel_ids = [];
    let need_status_filter = true;
    if (state !== null) {
        const { id, filter } = state;
        hotel_ids.push(id);
        need_status_filter = filter;
    } else {
        hotel_ids.push(...selectedHotels);
    }
    const [attachForm] = Form.useForm();
    const staff_id = localStorage.getItem(`x-dashboard-staffid`);
    const [tableData, setTableData] = useState([]);
    const [statusUpdateFormVisible, setStatusUpdateFormVisible] =
        useState(false);
    const [statusOptions, setStatusOptions] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [resolutionId, setResolutionId] = useState(0);
    const [attachVisible, setAttachVisible] = useState(false);
    const { selectedDates } = useContext(SettingContext);
    const [start_date, end_date] = selectedDates;
    const [form] = Form.useForm();
    const [formData, setFormData] = useState({
        start_date: formatDate(start_date),
        end_date: formatDate(end_date),
        staff_id,
        page: 0,
        limit: 10,
        hotel_ids,
        need_status_filter,
        all_selected: hotel_ids.length > 0 ? false : true,
        component_name: state?.component_name,
    });

    const {
        status: supportDetailStatus,
        data: supportDetailsRes,
        refetch: fetchSupportDetails,
    } = useQuery(
        "supportTicketDetails",
        async () => {
            return await HTTP("/dashboard/getSupportJiraTickets", {
                ...formData,
            });
        },
        { enabled: false },
    );

    const {
        status: resolutionStatus,
        data: resolutionDataRes,
        refetch: getResolutions,
    } = useQuery(
        "support-resolution",
        async () => {
            return await HTTP("/dashboard/getSupportJiraResolutions", {
                staff_id,
            });
        },
        { enabled: false },
    );

    useEffect(() => {
        if (state != null) {
            const { id } = state;
            setSelectedHotels([id]);
        }
        fetchSupportDetails();
        getResolutions();
    }, []);

    useEffect(() => {
        const [start_date, end_date] = selectedDates;
        setFormData({
            ...formData,
            start_date: formatDate(start_date),
            end_date: formatDate(end_date),
        });
    }, [selectedDates]);

    useEffect(() => {
        const hotel_ids = selectedHotels;
        setFormData({
            ...formData,
            hotel_ids,
            page: 0,
            all_selected: hotel_ids.length === 0 ? true : false,
        });
    }, [selectedHotels]);

    useEffect(() => {
        fetchSupportDetails();
    }, [formData]);

    useEffect(() => {
        if (supportDetailStatus === "success") {
            const { data: scanData, total } = supportDetailsRes;
            setTotalRecords(total);
            setTableData(scanData);
        }
    }, [supportDetailStatus, supportDetailsRes]);

    function handleTableChange(pagination, _, sorter, etra) {
        const { current, pageSize } = pagination;
        const { order, field } = sorter;
        const others = {
            page: current - 1,
            limit: pageSize,
        };
        if (order !== undefined) {
            others.sorting = order === "ascend" ? 1 : 2;
            others.sort_by = field;
        } else {
            delete formData.sorting;
            delete formData.sort_by;
        }
        const updatedFormData = { ...formData, ...others };
        updatedFormData.all_selected =
            updatedFormData.hotel_ids.length > 0 ? false : true;
        updatedFormData.start_date = formatDate(updatedFormData.start_date);
        updatedFormData.end_date = formatDate(updatedFormData.end_date);
        setFormData(updatedFormData);
    }

    async function showUpdateModal(ticket_key) {
        const formData = {
            staff_id,
            ticket_key,
        };
        const { success, data } = await HTTP(
            "/dashboard/getSupportJiraTicketStatus",
            formData,
        );
        if (success) {
            const resolveTransitionDetails = data.find(
                (e) => e.transition_name === "Resolve this issue",
            );
            if (resolveTransitionDetails) {
                setResolutionId(resolveTransitionDetails.transition_id);
            }
            setStatusOptions(data);
            setStatusUpdateFormVisible(true);
            form.setFieldsValue({ ticket_key });
        }
    }

    function showAttachModal(ticket_key) {
        attachForm.setFieldsValue({ ticket_key, staff_id });
        setAttachVisible(true);
    }

    const columns = [
        {
            title: "Key",
            key: "ticket_key",
            render: (text, record) => {
                return (
                    <a href={record.link} target="_blank" rel="noreferrer">
                        {record.ticket_key}
                    </a>
                );
            },
        },

        {
            title: "Hotel",
            dataIndex: "hotel_name",
            key: "hotel_name",
            sorter: () => {},
            width: 200,
        },
        {
            title: "Component",
            dataIndex: "component_name",
            key: "component_name",
            sorter: () => {},
            width: 150,
        },
        {
            title: "Date",
            dataIndex: "created_at",
            key: "created_at",
            sorter: () => {},
            width: 150,
        },
        {
            title: "Summary",
            dataIndex: "summary",
            key: "summary",
            sorter: () => {},
            width: 200,
        },
        {
            title: "Type",
            dataIndex: "issue_type",
            key: "issue_type",
            sorter: () => {},
            width: 150,
        },
        {
            title: "Priority",
            dataIndex: "priority",
            key: "priority",
            sorter: () => {},
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            sorter: () => {},
            width: 150,
        },
        {
            title: "Comments",
            render: (_, record) => {
                return record.comments?.length > 0 ? (
                    <ul>
                        {record.comments.map(({ comment_id, comment }) => (
                            <li key={comment_id}>
                                <Tooltip placement="rightTop" title={comment}>
                                    <span>
                                        {comment.length <= 30
                                            ? comment
                                            : `${comment.substring(0, 30)}...`}
                                    </span>
                                </Tooltip>
                            </li>
                        ))}
                    </ul>
                ) : null;
            },
            sorter: () => {},
            width: 250,
        },

        {
            title: "Reported By",
            dataIndex: "reporter_name",
            key: "reporter_name",
            sorter: () => {},
            width: 150,
        },
        {
            title: "Assigned To",
            dataIndex: "assignee_name",
            key: "assignee_name",
            sorter: () => {},
            width: 150,
        },
        {
            title: "Update Status",
            render: (text, record) => {
                return (
                    <Button
                        type="primary"
                        onClick={() => showUpdateModal(record.ticket_key)}
                    >
                        Update Status
                    </Button>
                );
            },
        },
        {
            title: "Attachements",
            render: (text, record) => {
                return (
                    <Button
                        type="primary"
                        onClick={() => showAttachModal(record.ticket_key)}
                    >
                        Upload Attachment
                    </Button>
                );
            },
        },
    ];

    function handleUpdateFormCancel() {
        setStatusUpdateFormVisible(false);
        setStatusOptions([]);
        form.resetFields();
    }

    async function updateStatus(values) {
        values.comment_body = Object.entries(values).reduce(
            (prev, [key, value]) => {
                if (
                    key !== "transition_id" &&
                    key !== "ticket_key" &&
                    key !== "comment_body"
                ) {
                    if (key == "Date/Time") {
                        prev += `${key} : ${
                            moment(value)._isValid ? formatDate(value) : ""
                        },`;
                    } else {
                        prev += `${key} : ${value || ""},`;
                    }
                }
                return prev;
            },
            ``,
        );
        const { success, message } = await HTTP(
            "/dashboard/updateSupportJiraTickets",
            { staff_id, ...values },
        );
        let type = "error";
        if (success) {
            type = "info";
        }
        notification[type]({
            message,
        });
        fetchSupportDetails();
        handleUpdateFormCancel();
    }

    async function uploadAttachment(values) {
        const formData = new FormData();
        formData.append("staff_id", staff_id);
        Object.entries(values).map(([key, value]) => {
            if (key == "file") {
                formData.append(
                    key,
                    value.fileList[0].originFileObj,
                    value.fileList[0].originFileObj.name,
                );
            } else {
                formData.append(key, value);
            }
        });
        const { success, message } = await HTTP(
            "/dashboard/jiraAddAttachment",
            formData,
        );
        attachForm.resetFields();
        setAttachVisible(false);
        let notification_type = "info";
        if (success === 0) {
            notification_type = "error";
        }
        notification[notification_type]({
            message: message,
        });
    }

    async function downalodReport() {
        const tempFormData = { ...formData };
        delete tempFormData["page"];
        delete tempFormData["limit"];
        const res = await HTTP("/dashboard/getSupportJiraTicketsReport", {
            ...tempFormData,
        });
        downloadCSV(res, `support_ticket_reports`);
    }

    return (
        <Card className="dashboard-container">
            <CustomHeader
                extraAfter={
                    (getUserRole() == "Super Admin" ||
                        getUserRole() == "Admin" ||
                        getUserRole() == "Management") && (
                        <Button
                            type="primary"
                            className="margin-right-5"
                            onClick={downalodReport}
                        >
                            Download
                        </Button>
                    )
                }
            />
            <Card className="box-shadow margin-10">
                {supportDetailStatus === "loading" ||
                supportDetailStatus === "idle" ? (
                    <Skeleton
                        active
                        title={false}
                        paragraph={{ rows: 20, width: 40 }}
                    />
                ) : (
                    <CustomTable
                        columns={columns}
                        tableData={tableData}
                        noOfrecords={totalRecords}
                        handleTableChange={handleTableChange}
                        rowKey="ticket_key"
                        className="support-table"
                        pagination={{
                            hideOnSinglePage: false,
                            responsive: true,
                            total: totalRecords,
                            defaultPageSize: formData.limit,
                            defaultCurrent: formData.page + 1 || 0,
                            showSizeChanger: true,
                            pageSizeOptions: [10, 20, 50],
                            showTotal: (totalRecords, range) =>
                                `${range[0]}-${range[1]} of ${totalRecords} records`,
                            current: formData?.page + 1 || 0,
                        }}
                        isScroll
                    />
                )}
            </Card>

            <Modal
                title="Attach File"
                open={attachVisible}
                onCancel={() => setAttachVisible(false)}
                destroyOnClose={true}
                footer=""
            >
                <Form form={attachForm} onFinish={uploadAttachment}>
                    <Form.Item name="file" valuePropName="file">
                        <Dragger
                            beforeUpload={() => false}
                            id="file"
                            name="file"
                            maxCount={1}
                        >
                            <p className="ant-upload-drag-icon">
                                <InboxOutlined />
                            </p>
                            <p className="ant-upload-text">
                                Click or drag file to this area to upload
                            </p>
                        </Dragger>
                    </Form.Item>

                    <Form.Item name="ticket_key" className="hidden">
                        <Input id="id" />
                    </Form.Item>

                    <Form.Item
                        wrapperCol={{
                            offset: 20,
                            span: 16,
                        }}
                    >
                        <Button type="primary" htmlType="submit">
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Update Status"
                open={statusUpdateFormVisible}
                onCancel={handleUpdateFormCancel}
                destroyOnClose={true}
                footer=""
            >
                <Form
                    form={form}
                    name="basic"
                    labelCol={{
                        span: 8,
                    }}
                    wrapperCol={{
                        span: 16,
                    }}
                    onFinish={updateStatus}
                    autoComplete="off"
                >
                    <Form.Item
                        label="Status"
                        name="transition_id"
                        rules={[
                            {
                                required: true,
                                message: "Please Select Status..!",
                            },
                        ]}
                    >
                        <Select id="transition_id">
                            {statusOptions.map((e) => (
                                <Option
                                    key={e.transition_id}
                                    value={e.transition_id}
                                >
                                    {e.transition_name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        shouldUpdate={(prevValues, currentValues) =>
                            prevValues.transition_id !==
                            currentValues.transition_id
                        }
                    >
                        {({ getFieldValue }) =>
                            getFieldValue("transition_id") == resolutionId && (
                                <Form.Item
                                    label="Resoltion"
                                    name="resolution_name"
                                    rules={[
                                        {
                                            required: true,
                                            message:
                                                "Please Select Resolution..!",
                                        },
                                    ]}
                                >
                                    <Select id="resolution_name">
                                        {resolutionStatus === "success" &&
                                            resolutionDataRes.data.length > 0 &&
                                            resolutionDataRes.data.map((e) => (
                                                <Option
                                                    key={e.resolution_name}
                                                    value={e.resolution_name}
                                                >
                                                    {e.resolution_name}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            )
                        }
                    </Form.Item>

                    <Form.Item
                        label="Comment"
                        name="comment_body"
                        rules={[
                            {
                                required: true,
                                message: "Please Add Commnet..!",
                            },
                        ]}
                    >
                        <TextArea showCount maxLength={80} />
                    </Form.Item>

                    <Form.Item label="Issue Reported" name="Issue Reported">
                        <Input />
                    </Form.Item>

                    <Form.Item label="Date/Time" name="Date/Time">
                        <DatePicker
                            allowClear={false}
                            format={"MM/DD/YYYY"}
                            disabledDate={(current) =>
                                current && current > moment().endOf("day")
                            }
                        />
                    </Form.Item>

                    <Form.Item label="Notes" name="Notes">
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Client Information"
                        name="Client Information"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Troubleshoot Issue"
                        name="Troubleshoot Issue"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Steps that fixed issue"
                        name="Steps that fixed issue"
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        label="Hardware Changed?"
                        name="Hardware Changed?"
                    >
                        <Select>
                            <Option>YES</Option>
                            <Option>NO</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item name="ticket_key" className="hidden">
                        <Input
                            id="id"
                            className="upcoming-form-inputs"
                            disabled
                        ></Input>
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{
                            offset: 20,
                            span: 16,
                        }}
                    >
                        <Button type="primary" htmlType="submit">
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
}
export { SupportDetailsTable };
